import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { StepIndicator, type WizardStep } from './StepIndicator';
import { StepConnect } from './StepConnect';
import { StepSettings } from './StepSettings';
import { StepProducts } from './StepProducts';
import { StepImport } from './StepImport';
import { StepDone } from './StepDone';
import { StepBilling } from './StepBilling';
import type { OnboardingStatus } from './types';
import { LoadingState, ErrorState, UnauthState } from './wizardStates';

/**
 * Onboarding wizard — the Pages port of the embedded Onboarding.tsx App Page.
 *
 * Flow: Connect → Map products → Import rules → Done. The wizard reads its
 * initial state from GET /onboarding/status (proxied via the Pages /api proxy
 * with the session cookie). All backend calls go through the same proxy; no
 * Worker URL or portalId is needed client-side (the session cookie scopes to
 * one tenant).
 *
 * Stripe Connect here is a single server-side redirect (no tab dance): the
 * "Connect" button hits GET /onboarding/stripe-app, which returns a URL;
 * we navigate the browser there, Stripe redirects back to the Worker callback,
 * which completes server-side. The old "open in new tab + I've connected
 * button" UX is gone.
 */
export function OnboardingWizard() {
  const [step, setStep] = useState<WizardStep>('connect');
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  // Import completion stats shown on the Done step (QA-F #5 — print stats).
  // Captured from the workflow-status payload when the import is found complete.
  const [importStats, setImportStats] = useState<{
    totalProcessed: number;
    totalFailed: number;
  } | null>(null);
  // True while StepImport's progress view is showing (import running OR just
  // completed). When set, the step INDICATOR advances to 'done' so "Import
  // rules" greens and "Done" highlights blue — even though the progress view
  // still renders inside the import step (QA round 2 #1).
  const [importProgressVisible, setImportProgressVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauth, setUnauth] = useState(false);
  // Dismissible Stripe-connect error banner (2026-09-19): the connect
  // callback 302s back here with ?stripe_error=… (e.g. publisher-account
  // self-install, token exchange failure). Rendered as a banner the user can
  // dismiss — previously the param was silently ignored and the URL stayed
  // polluted. Consumed once on mount; the param is cleaned from the URL so a
  // refresh doesn't resurrect the message.
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const se = params.get('stripe_error');
    if (se) {
      setStripeError(se);
      params.delete('stripe_error');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}`
      );
    }
  }, []);

  // M30: Stripe-first arrival — the user installed the app from Stripe's
  // marketplace and was routed through login (stripe_notice param). Detected
  // ONCE and persisted in sessionStorage so it survives the HubSpot OAuth
  // round-trip + the wizard's Connect-Stripe redirect; clears when the wizard
  // completes. Drives StepConnect's one-shot auto-continue.
  const stripeFirstRef = useRef<boolean>(false);
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_notice') !== null) {
      sessionStorage.setItem('ampdeck_stripe_first', '1');
    }
    stripeFirstRef.current =
      sessionStorage.getItem('ampdeck_stripe_first') === '1';
    return () => {
      if (stripeFirstRef.current && stepRef.current === 'done') {
        sessionStorage.removeItem('ampdeck_stripe_first');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial status fetch. Determines the landing step: Connect (no Stripe) →
  // Sync settings → Products → Import → Done. If a historical import is already
  // running (the user left and returned — QA #3), jump straight to the Import
  // step so live progress is shown; if it already completed (QA-F #5), jump to
  // Done; the durable Workflow instance keeps advancing independently of the UI.
  useEffect(() => {
    let cancelled = false;
    api
      .get<OnboardingStatus>('onboarding/status')
      .then(async (data) => {
        if (cancelled) return;
        setStatus(data);
        if (!data.stripeConnected) {
          setStep('connect');
          return;
        }
        // Stripe connected — check the import (Workflow instance) status to
        // land on the right step. Best-effort: any fetch failure falls back to
        // the settings step. (QA-F #5: a tenant who completed the import must
        // land on the Done step when they re-open /onboarding — previously a
        // completed instance still showed the Import phase with a continue btn.)
        try {
          const wf = await api.get<{
            missing: boolean;
            status: string;
            progress?: {
              phase?: string;
              totalProcessed?: number;
              totalFailed?: number;
            } | null;
          }>('onboarding/bootstrap/workflow-status');
          if (cancelled) return;
          // Completed cleanly (platform 'complete' OR DO phase 'done') → Done.
          // The import is finished and the dashboard is unlocked.
          const completed =
            wf.status === 'complete' || wf.progress?.phase === 'done';
          // A non-missing, non-complete instance is either still running or
          // failed — surface it on the Import step (live progress / failure).
          if (!wf.missing && !completed) {
            setStep('import');
            return;
          }
          if (!wf.missing && completed) {
            setImportStats({
              totalProcessed: wf.progress?.totalProcessed ?? 0,
              totalFailed: wf.progress?.totalFailed ?? 0,
            });
            setStep('done');
            return;
          }
          // missing = no instance yet → proceed to the settings step.
        } catch {
          /* non-fatal — fall through to the default step */
        }
        setStep('settings');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) setUnauth(true);
        else setError(e instanceof Error ? e.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToStep = (next: WizardStep) => {
    setStep(next);
    // Refresh status when moving between steps so the next step sees truth.
    api
      .get<OnboardingStatus>('onboarding/status')
      .then(setStatus)
      .catch(() => {
        /* non-fatal — status is stale until next load */
      });
  };

  if (loading) return <LoadingState label="Checking your setup status…" />;
  if (unauth) return <UnauthState />;
  if (error) return <ErrorState message={error} />;

  // M21 T5c: billing gate. When billingRequired is true, show ONLY the billing
  // step — no step indicator, no wizard steps. After Checkout completes, the
  // user returns to /onboarding?billing=success; the status refresh sees
  // billingRequired=false and the wizard proceeds normally.
  if (status?.billingRequired) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900">
            Set up Stripe Sync
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Start your free trial to begin setup.
          </p>
        </div>
        <StepBilling />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">
          Set up Stripe Sync
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          Connect your Stripe account, map your products, and import your history.
        </p>
      </div>

      {/* Stripe-connect error from the OAuth callback (?stripe_error=…) —
          dismissible; the wizard stays fully usable beneath it. */}
      {stripeError && (
        <div
          role="alert"
          className="relative mb-4 rounded-lg border border-danger-300 bg-danger-500/10 px-4 py-3 pr-10 text-sm text-danger-700"
        >
          <strong>Stripe connection failed.</strong> {stripeError}
          <button
            type="button"
            onClick={() => setStripeError(null)}
            aria-label="Dismiss"
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-danger-400 transition-colors hover:bg-danger-500/20 hover:text-danger-600"
          >
            ×
          </button>
        </div>
      )}

      {/* When the import progress view is showing, advance the indicator to
          'done' (Import rules greens, Done highlights blue). The progress view
          still renders inside the import step; this is an INDICATOR-only
          override (QA round 2 #1). */}
      <StepIndicator
        currentStep={importProgressVisible && step === 'import' ? 'done' : step}
        onStepClick={(s) => {
          // Only the completed Connect step is navigable (the escape hatch
          // to "Use a different account" lives on its connected card).
          if (s === 'connect') goToStep('connect');
        }}
      />

      {step === 'connect' && (
        <StepConnect status={status} onConnected={() => goToStep('settings')} stripeFirst={stripeFirstRef.current} />
      )}
      {step === 'settings' && (
        <StepSettings onDone={() => goToStep('products')} onBack={() => goToStep('connect')} />
      )}
      {step === 'products' && (
        <StepProducts onDone={() => goToStep('import')} onBack={() => goToStep('settings')} />
      )}
      {step === 'import' && (
        <StepImport
          status={status}
          onProgressVisibleChange={setImportProgressVisible}
          onBack={() => goToStep('products')}
        />
      )}
      {step === 'done' && <StepDone status={status} importStats={importStats} />}
    </div>
  );
}
